<div class="inmate-details-container">
  <forge-scaffold>
    <forge-toolbar slot="header">
      <h1 id="dialog-title" slot="start">Inmate record</h1>
      <forge-icon-button slot="end" aria-label="Close dialog" (click)="closeDialog()">
        <forge-icon name="close"></forge-icon>
      </forge-icon-button>
    </forge-toolbar>

    <div slot="body" class="inmate-details-body">
      <div class="inmate-detail-banner">
        <img [src]="(hideMugShots ? null : mugshotImageDetails?.imageUrls?.size320) ?? 'assets/no-image-headshot.png'"
          alt="Mugshot"
          [class.clickable]="!hideMugShots && !!mugshotImageDetails?.imageUrls"
          (click)="openEnlargedMugshot()"
          (keydown.enter)="openEnlargedMugshot()"
          (keydown.space)="$event.preventDefault(); openEnlargedMugshot()"
          [attr.tabindex]="hideMugShots || !mugshotImageDetails?.imageUrls ? null : '0'"
          [attr.role]="hideMugShots || !mugshotImageDetails?.imageUrls ? null : 'button'"
          [attr.aria-label]="hideMugShots || !mugshotImageDetails?.imageUrls ? 'Inmate mugshot' : 'View enlarged mugshot'"
          [attr.title]="hideMugShots || !mugshotImageDetails?.imageUrls ? null : 'Click to enlarge'"
          (error)="onImageError()" />
        <div class="inmate-name">{{ inmate.name.fullName }}</div>
        <div class="inmate-pid"><span class="pid-label">PID #</span><span class="pid-number">{{ inmate.pid || 'N/A' }}</span></div>
        @if (statusDisplay && statusDisplay !== 'Unknown') {
          <div class="inmate-status">
            <span class="status-pill" [ngClass]="statusDisplay | inmateStatusClass">{{ statusDisplay }}</span>
          </div>
        }
      </div>
      
      <forge-tab-bar data-aria-label="Inmate Detail Tabs" [activeTab]="activeTabIndex"
        (forge-tab-bar-change)="onTabChange($event)">
        <forge-tab>Details</forge-tab>
        <forge-tab>Offenses <forge-badge slot="end" [strong]="activeTabIndex === 1"
            [theme]="activeTabIndex === 1 ? 'info' : 'info-secondary'">{{ inmate.offenses?.length || 0 }}</forge-badge></forge-tab>
        <forge-tab>Parole info</forge-tab>
      </forge-tab-bar>
      
      <forge-view-switcher [index]="activeTabIndex">
        <forge-view>
          <div class="detail-section">
            <h3 class="section-title">DEMOGRAPHICS</h3>
            <div class="demographics">
              <div class="demographics-item">
                <span class="label">Race</span>
                <span class="value" [ngClass]="{ 'no-value': !raceLabel }">{{ raceLabel | na }}</span>
              </div>
              <div class="demographics-item">
                <span class="label">Gender</span>
                <span class="value" [ngClass]="{ 'no-value': !genderLabel }">{{ genderLabel | na }}</span>
              </div>
              <div class="demographics-item dob-item">
                <span class="label">Date of birth</span>
                <span class="value">{{ inmate.dateOfBirth | date:'MMM d, yyyy':'UTC'}} (Age {{ calculatedAge }})</span>
              </div>
              <div class="demographics-item">
                <span class="label">Hair</span>
                <span class="value" [ngClass]="{ 'no-value': !hairColorLabel }">{{ hairColorLabel | na }} </span>
              </div>
              <div class="demographics-item">
                <span class="label">Eyes</span>
                <span class="value" [ngClass]="{ 'no-value': !eyeColorLabel }"> {{ eyeColorLabel | na }} </span>
              </div>
              <div class="demographics-item">
                <span class="label">Weight</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.weight }">{{ inmate.weight | na }}</span>
              </div>
              <div class="demographics-item">
                <span class="label">Height</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.height }">{{ inmate.height | na }}</span>
              </div>
              <div class="demographics-item aliases-item">
                <span class="label">Aliases</span>
                <span class="value" [ngClass]="{ 'no-value': formattedAliases === 'n/a' }">{{ formattedAliases }}</span>
              </div>
            </div>
          </div>
          
          <div class="detail-section">
            <h3 class="section-title">INCARCERATION DETAILS</h3>
            <div class="incarceration-details">
              <div class="incarceration-item">
                <span class="label">DOC #</span>
                <span class="value">{{ inmate.docNumber | na }}</span>
              </div>
              <div class="incarceration-item">
                <span class="label">Location</span>
                <span class="value">{{ locationDisplay | na }}</span>
              </div>
              <div class="incarceration-item">
                <span class="label">Offender status</span>
                <span class="value">{{ statusDisplay | na }}</span>
              </div>
              <div class="incarceration-item">
                <span class="label">Risk assessment</span>
                <span class="value">{{ (riskAssessmentCode | riskAssessment) | na }}</span>
              </div>
              @if (inmate.supervisionStart) {
                <div class="incarceration-item">
                  <span class="label">Supervision start</span>
                  <span class="value">{{ inmate.supervisionStart | date:'MMM d, yyyy':'UTC' | na }}</span>
                </div>
              }
              @if (inmate.supervisionEnd) {
                <div class="incarceration-item">
                  <span class="label">Supervision end</span>
                  <span class="value">{{ inmate.supervisionEnd | date:'MMM d, yyyy':'UTC' | na }}</span>
                </div>
              }
              @if (inmate.custodyStartDate) {
                <div class="incarceration-item">
                  <span class="label">Institution start</span>
                  <span class="value">{{ inmate.custodyStartDate | date:'MMM d, yyyy':'UTC' | na }}</span>
                </div>
              }
              @if (inmate.classification) {
                <div class="incarceration-item">
                  <span class="label">Classification</span>
                  <span class="value">{{ inmate.classification | na }}</span>
                </div>
              }
              @if (inmate.sentenceMaxDate) {
                <div class="incarceration-item">
                  <span class="label">Max. sentence expiration</span>
                  <span class="value">{{ inmate.sentenceMaxDate | date:'MMM d, yyyy':'UTC' | na }}</span>
                </div>
              }
              @if (inmate.sentenceMinDate) {
                <div class="incarceration-item">
                  <span class="label">Min. sentence expiration</span>
                  <span class="value">{{ inmate.sentenceMinDate | date:'MMM d, yyyy':'UTC' | na }}</span>
                </div>
              }
              @if (inmate.goodTimeReleaseDate) {
                <div class="incarceration-item">
                  <span class="label">Expected time to serve</span>
                  <span class="value">{{ inmate.goodTimeReleaseDate | date:'MMM d, yyyy':'UTC' | na }}</span>
                </div>
              }
              @if (inmate.paroleEligibilityDate) {
                <div class="incarceration-item">
                  <span class="label">Parole eligibility</span>
                  <span class="value">{{ inmate.paroleEligibilityDate | date:'MMM d, yyyy':'UTC' | na }}</span>
                </div>
              }
            </div>
          </div>
        </forge-view>
        
        <forge-view>
          <div class="gray-section">
             <div class="gray-header">
               <h3 class="gray-title">CONVICTIONS AND OFFENSES</h3>
             </div>
             <div class="gray-list">
               @for (offense of inmate.offenses; track $index) {
                 <forge-card class="offense-card">
                   <forge-expansion-panel>
                     <div slot="header" role="button" tabindex="0">
                        <forge-label-value class="offense-header-text">
                          @if (offense.offenseTypeName || offense.degree) {
                            <label slot="label">{{ offense.offenseTypeName || offense.degree }}</label>
                          }
                          <span slot="value" [id]="'offense-desc-' + $index" [title]="offense.description || null">{{ offense.description }}</span>
                          <forge-tooltip [target]="'#offense-desc-' + $index">{{ offense.description }}</forge-tooltip>
                        </forge-label-value>
                       <forge-open-icon></forge-open-icon>
                     </div>
                     <forge-divider></forge-divider>
                     <div class="offense-details">
                       <div class="offense-grid">
                          <forge-label-value>
                            <span slot="label" class="label">Indictment #</span>
                            <span slot="value" class="value">{{ offense.indictmentNumber | na }}</span>
                          </forge-label-value>
                          <forge-label-value>
                            <span slot="label" class="label">Crime date</span>
                            <span slot="value" class="value">{{ (offense.crimeDate | date:'MMM d, yyyy':'UTC') || 'N/A' }}</span>
                          </forge-label-value>
                          <forge-label-value>
                            <span slot="label" class="label">Conviction date</span>
                            <span slot="value" class="value">{{ (offense.convictionDate | date:'MMM d, yyyy':'UTC') || 'N/A' }}</span>
                          </forge-label-value>
                          <forge-label-value>
                            <span slot="label" class="label">Conviction county</span>
                            <span slot="value" class="value">{{ offense.convictionCounty | na }}</span>
                          </forge-label-value>
                          <forge-label-value>
                            <span slot="label" class="label">Indictment count</span>
                            <span slot="value" class="value">{{ offense.indictmentCount | na }}</span>
                          </forge-label-value>
                          <forge-label-value>
                            <span slot="label" class="label">KRS Code</span>
                            <span slot="value" class="value">{{ offense.krsCode | na }}</span>
                          </forge-label-value>
                          <forge-label-value>
                            <span slot="label" class="label">Felony class</span>
                            <span slot="value" class="value">{{ offense.felonyClass | na }}</span>
                          </forge-label-value>
                          <forge-label-value>
                            <span slot="label" class="label">Sentence length</span>
                            <span slot="value" class="value">{{ offense.sentenceLength | na }}</span>
                          </forge-label-value>
                       </div>
                     </div>
                   </forge-expansion-panel>
                 </forge-card>
               }
               @if (!inmate.offenses || inmate.offenses.length === 0) {
                 <div>No offenses found.</div>
               }
             </div>
          </div>
        </forge-view>
        
        <forge-view>
          <div class="gray-section">
             <div class="gray-header">
               <h3 class="gray-title">PAROLE HEARINGS</h3>
               <p class="gray-subtitle">Hearings are conducted 60 days prior to parole date</p>
             </div>
             <div class="gray-list">
               @for (hearing of inmate.hearings; track hearing) {
                 <forge-card class="offense-card">
                   <forge-expansion-panel>
                     <div slot="header" role="button" tabindex="0">
                       <div class="parole-grid">
                         <forge-label-value>
                           <span slot="label" class="label">Hearing date</span>
                           <span slot="value" class="value" [ngClass]="{ 'no-value': !hearing.hearingDate }">{{ (hearing.hearingDate | date:'MMM d, yyyy':'UTC') || 'n/a' }}</span>
                         </forge-label-value>
                         <forge-label-value>
                           <span slot="label" class="label">Hearing action</span>
                           <span slot="value" class="value">{{ hearing.hearingAction }}</span>
                         </forge-label-value>
                       </div>
                       <forge-open-icon></forge-open-icon>
                     </div>
                     <forge-divider></forge-divider>
                     <div class="offense-details">
                       <div class="parole-details-grid">
                         <forge-label-value>
                           <span slot="label" class="label">Months deferred</span>
                           <span slot="value" class="value" [ngClass]="{ 'no-value': !hearing.monthsDeferred }">{{ hearing.monthsDeferred || 'n/a' }}</span>
                         </forge-label-value>
                         <forge-label-value>
                           <span slot="label" class="label">Next parole eligibility</span>
                           <span slot="value" class="value" [ngClass]="{ 'no-value': !hearing.nextParoleEligibility }">{{ (hearing.nextParoleEligibility | date:'MMM d, yyyy':'UTC') || 'n/a' }}</span>
                         </forge-label-value>
                         <forge-label-value>
                           <span slot="label" class="label">Proposed release date</span>
                           <span slot="value" class="value" [ngClass]="{ 'no-value': !hearing.proposedReleaseDate }">{{ (hearing.proposedReleaseDate | date:'MMM d, yyyy':'UTC') || 'n/a' }}</span>
                         </forge-label-value>
                       </div>
                     </div>
                   </forge-expansion-panel>
                 </forge-card>
               }
               @if (!inmate.hearings || inmate.hearings.length === 0) {
                 <div>No parole hearings found.</div>
               }
             </div>
          </div>
        </forge-view>
      </forge-view-switcher>
    </div>
    
    <div slot="footer" class="footer-action" *ngIf="activeTabIndex === 0 && vineUrl">
      <forge-button variant="outlined" class="notify-btn" (click)="openVineUrl()" (keydown.enter)="openVineUrl()" (keydown.space)="$event.preventDefault(); openVineUrl()">
        <forge-icon name="notifications" slot="start" class="notify-icon"></forge-icon>
        <span class="notify-text">Get notified of inmate's custody status</span>
      </forge-button>
    </div>
  </forge-scaffold>
</div>
